const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

export interface VerifyResponse {
  jwt: string;
  user: { id: string; telegramId: string; language: string; firstName: string | null };
}

export class AuthVerifyError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Populated when status===403 and error==='account_pending_deletion'. */
    public pendingDeletion?: { deletedAt: string; gracePeriodDays: number },
  ) {
    super(message);
    this.name = 'AuthVerifyError';
  }
}

export async function postAuthVerify(initData: string): Promise<VerifyResponse> {
  const res = await fetch(`${API_BASE}/api/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  if (!res.ok) {
    // T25 — translate 403 account_pending_deletion into a typed error so the
    // splash/AuthGate can render the pending-deletion banner.
    if (res.status === 403) {
      const parsed = (await res.json().catch(() => null)) as
        | { error?: string; deletedAt?: string; gracePeriodDays?: number }
        | null;
      if (parsed?.error === 'account_pending_deletion' && parsed.deletedAt) {
        throw new AuthVerifyError(403, 'account_pending_deletion', {
          deletedAt: parsed.deletedAt,
          gracePeriodDays: parsed.gracePeriodDays ?? 30,
        });
      }
    }
    const body = await res.text().catch(() => '');
    throw new AuthVerifyError(res.status, `auth verify failed: ${res.status} ${body}`);
  }
  return (await res.json()) as VerifyResponse;
}

export async function apiFetch(path: string, jwt: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...init.headers, authorization: `Bearer ${jwt}` },
  });
}

/**
 * Multipart upload helper. Used by P1.5c selfie upload.
 *
 * Does NOT set `Content-Type` — browser sets it automatically with the
 * multipart boundary. Spreading `init.headers` would clobber the
 * authorization header, so it's set explicitly.
 */
export async function apiUpload(path: string, jwt: string, formData: FormData): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${jwt}` },
    body: formData,
  });
}
